import type { ContentTemplate, Field, Stylepack, TemplateData } from '@/types/stylepacks.types';
import db from '@/utils/indexed-db';
import { isObject } from 'underscore';
import type { GraphicItem } from '../components/video-list-graphic.types';
import type { GraphicInterface } from '@/types/greenroom/graphics.types';
import type { Shot } from '@/types/greenroom/shots.types';
import type { MediaItem } from '@/types/greenroom/media.types';
import { arrayToMap } from '@/utils/array-to-map';

export const isTemplateData = (data: unknown): data is TemplateData => {
  return isObject(data) && 'variables' in data && Array.isArray(data.variables);
};

export const extractVariablesFromStylepack = (video: ContentTemplate): Field[] => {
  if (isTemplateData(video.data)) {
    return video.data.variables;
  }

  return [];
};

const onlyColors = (field: Field) => field.type === 'color';
const defaultValue = (field: Field) => field.default;

export const extractColorsFromStylepack = (stylepack: Stylepack) => {
  return Array.from(
    new Set(stylepack.videos.flatMap(extractVariablesFromStylepack).filter(onlyColors).map(defaultValue))
  );
};

export const getContentTemplateDuration = (video: ContentTemplate) => {
  return new Promise<{ video: ContentTemplate; duration: number }>((resolve, reject) => {
    if (!video.videoUrl) {
      reject(video);
      return;
    }

    if (isTemplateData(video.data) && video.data.duration) {
      resolve({ duration: video.data.duration, video });
      return;
    }

    // duration is not known but there is a media url for it - load the duration from the
    // video's metadata (should not load all data unless the network forces it)
    const onLoadedMetadata = () => {
      resolve({ duration: el.duration, video });
    };

    const onError = (e: ErrorEvent) => {
      console.error(e);
      reject(video);
    };

    // create video element and load only the metadata
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('error', onError);
    el.src = video.videoUrl;
  });
};

export const getGraphicDuration = (graphicItem: GraphicItem) => {
  return new Promise<{ video: GraphicItem; duration: number }>((resolve, reject) => {
    if (!graphicItem.videoUrl) {
      reject(graphicItem);
    }

    // duration is not known but there is a media url for it - load the duration from the
    // video's metadata (should not load all data unless the network forces it)
    const onLoadedMetadata = () => {
      resolve({ duration: el.duration, video: graphicItem });
    };

    const onError = (e: ErrorEvent) => {
      console.error(e);
      reject(graphicItem);
    };

    // create video element and load only the metadata
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('error', onError);
    el.src = graphicItem.videoUrl;
  });
};

export const addToRecentStylepacks = (stylepackId: string) => {
  db.get<string[]>('recent', 'stylepacks')
    .then((recent) => {
      let newRecent: string[];

      if (recent) {
        // convert recent array to set and delete this one...
        const recentSet = new Set(recent.data);
        recentSet.delete(stylepackId);

        // then unshift it onto the front of the recent array
        newRecent = [stylepackId, ...Array.from(recentSet)];
      } else {
        newRecent = [stylepackId];
      }

      db.put({ type: 'recent', uuid: 'stylepacks', data: newRecent }).catch(console.error);
    })
    .catch(console.error);
};

export const createTemplateTypeToShotMap = (
  greenroomGraphics: GraphicInterface[] | null,
  greenroomShots: Shot[] | null,
  greenroomMedia: MediaItem[] | null
) => {
  const shotMap: Record<string, Shot[]> = {};

  // create a map of template types to the shots that use them
  // we are only using the first shot using the template type, but it is an array here
  // in case this needs to change
  if (greenroomGraphics && greenroomShots && greenroomMedia) {
    const graphicIdMap = arrayToMap('id', greenroomGraphics) as Map<number, GraphicInterface>;
    const mediaIdMap = arrayToMap('id', greenroomMedia) as Map<number, MediaItem>;

    // iterate over all shots
    for (const shot of greenroomShots) {
      // shot contains graphics, iterate over them and add the template types to the map
      if (shot.shot_data.shotGraphics) {
        for (const g of shot.shot_data.shotGraphics) {
          const graphic = graphicIdMap.get(g);
          if (graphic?.template_type) {
            shotMap[graphic.template_type] = [...(shotMap[graphic.template_type] || []), shot];
          }
        }
      }

      // shot contains feeds, if the feed contains a template type it's from a stylepack
      // so map the stylepack id to the shot for lookup in other places
      if (shot.shot_data.shotFeeds) {
        for (const feed of shot.shot_data.shotFeeds) {
          if (feed.media) {
            const mediaItem = mediaIdMap.get(feed.media);
            if (mediaItem?.template_type) {
              shotMap[mediaItem.template_type] = [...(shotMap[mediaItem.template_type] || []), shot];
            }
          }
        }
      }
    }
  }

  return shotMap;
};
